import { PopoverContent } from '@radix-ui/react-popover';
import { type PropsWithChildren, type ReactNode, useState } from 'react';
import IconButton from '@/components/Button/IconButton';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Popover } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/cn';
import { StatCard, StatCardHighlight, StatCardPrimaryValue, StatCardSecondaryValue, StatCardTitle, StatCardValueContainer } from '..';
type Props = PropsWithChildren<{
  title: string;
  help?: {
    tooltip: string;
    content: ReactNode;
  };
  /** タイトル右に置く任意のアクション（「もっと見る」リンク等） */
  action?: ReactNode;
  highlight: ReactNode;
  className?: string;
}>;

/**
 * Overviewで表示する統計情報を表示するためのカードです。
 * 基本的には、このカードをベースにしてその他のバリエーションを実装するとコスパ良いかなと思い実装しました。
 */
export function StatCardBase({
  title,
  help,
  action,
  highlight,
  children,
  className
}: Props) {
  return <StatCard className={cn(className)}>
      <VStack className="h-full">
        <HStack className="w-full items-center justify-between">
          <StatCardTitle>{title}</StatCardTitle>
          <HStack className="items-center gap-1">
            {action}
            {help && <Help tooltip={help.tooltip} content={help.content} />}
          </HStack>
        </HStack>
        <Spacer variant="s" />
        {highlight}
        {children && <>
            <Separator className="my-2" />
            {children}
          </>}
      </VStack>
    </StatCard>;
}
function Help({
  tooltip,
  content
}: {
  tooltip: string;
  content: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return <>
      <IconButton icon="HelpCircleIcon" aria-label={tooltip} variant="ghost" tooltip={tooltip} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverContent>{content}</PopoverContent>
      </Popover>
    </>;
}
export function Highlight(props: {
  prefix?: string;
  label: string | number;
  suffix?: string;
}) {
  const {
    prefix,
    label,
    suffix
  } = props;
  return <StatCardHighlight>
      <StatCardValueContainer>
        {prefix && <StatCardSecondaryValue>{prefix}</StatCardSecondaryValue>}
        <StatCardPrimaryValue>{label}</StatCardPrimaryValue>
        {suffix && <StatCardSecondaryValue>{suffix}</StatCardSecondaryValue>}
      </StatCardValueContainer>
    </StatCardHighlight>;
}